import { observable, reaction, action } from 'mobx';
import { ZipApi } from '../../lib/ZipApi';
import { getApiErrorMessage } from '../../lib/ZipApiError';
import { directoryStore } from '../DirectoryStore';
import { OptimizelyProduct } from '../../types/DataSyncItem';
import { HubApp } from '../../types/DataSyncItem';
import { Maybe } from '../../utils/maybe';
import ProductStoreFactory from './ProductStoreFactory';

export interface Product {
  text: string;
  value: string;
  iconPath?: string;
}

export interface ProductInstance {
  text: string;
  value: string;
  iconPath?: string;
  unavailable?: boolean;
}

/** Shown in place of an app's real icon when its version metadata can't be loaded. */
const FALLBACK_APP_ICON = '/assets/app_icon.svg';

export abstract class DataSyncProductStore {
  @observable protected _hubAppsLoading = false;
  @observable protected _hubApps: HubApp[] = [];
  @observable protected _hubAppsError: string | null = null;

  constructor() {
    reaction(() => directoryStore.trackerId, () => {
      this.fetchHubApps();
    }, {fireImmediately: true});
  }

  public get productInstancesLoading(): boolean {
    return ProductStoreFactory.getProductInstancesStore().productInstancesLoading;
  }

  public get productInstancesError(): string | null {
    return ProductStoreFactory.getProductInstancesStore().productInstancesError;
  }

  public get hubAppsError(): string | null {
    return this._hubAppsError;
  }

  public abstract getProductList(): Product[];

  public getInstanceList(
    productName: OptimizelyProduct,
    configuredInstanceId?: string,
    configuredInstanceName?: string
  ): ProductInstance[] {
    let instances: ProductInstance[] = [];

    if (productName === OptimizelyProduct.HUB_APP) {
      instances = this._hubApps.map((app) => {
        return {
          text: app.name,
          value: app.app_id,
          iconPath: app.icon_url || FALLBACK_APP_ICON
        };
      });
    } else {
      const productInstances = ProductStoreFactory.getProductInstancesStore().productInstances;
      const product = productInstances?.find((obj) => obj.product === productName);
      if (!product) {
        instances = [];
      } else {
        instances = product.instances.map((instance) => {
          return {
            text: instance.name,
            value: instance.id,
          };
        });
      }
    }

    // If configured instance is provided and not in available list, prepend it as unavailable
    if (configuredInstanceId && configuredInstanceName) {
      const isAvailable = instances.some((inst) => inst.value === configuredInstanceId);
      if (!isAvailable) {
        instances.unshift({
          text: configuredInstanceName,
          value: configuredInstanceId,
          unavailable: true,
          // Use default icon for unavailable Hub Apps
          iconPath: productName === OptimizelyProduct.HUB_APP ? '/assets/app_icon.svg' : undefined,
        });
      }
    }

    return instances;
  }

  public get hubAppsLoading(): boolean {
    return this._hubAppsLoading;
  }

  /**
   * Reassigned wholesale by `fetchHubApps`, so its identity is a usable React dependency.
   */
  public get hubApps(): HubApp[] {
    return this._hubApps;
  }

  public getInstance(
    productName: OptimizelyProduct,
    instanceId: string
  ): ProductInstance | undefined {
    if (productName === OptimizelyProduct.HUB_APP) {
      return Maybe.of(this._hubApps)
        .find((apps) => apps.find((app) => app.app_id === instanceId))
        .map((app) => ({
          text: app.name,
          value: app.app_id,
          iconPath: app.icon_url,
        }))
        .unwrap();
    }

    const productInstances = ProductStoreFactory.getProductInstancesStore().productInstances;
    return Maybe.of(productInstances)
      .find((products) => products.find((p) => p.product === productName))
      .find((product) => product.instances.find((i) => i.id === instanceId))
      .map((instance) => ({
        text: instance.name,
        value: instance.id,
      }))
      .unwrap();
  }

  public isInstanceAvailable(
    productName: OptimizelyProduct,
    instanceId: string
  ): boolean {
    if (!instanceId) {
      return true;
    }

    if (productName === OptimizelyProduct.HUB_APP) {
      return this._hubApps.some((app) => app.app_id === instanceId);
    } else {
      return Maybe.of(ProductStoreFactory.getProductInstancesStore().productInstances)
        .find((products) => products.find((p) => p.product === productName))
        .map((product) => product.instances.some((instance) => instance.id === instanceId))
        .unwrapOr(false);
    }
  }

  protected abstract getAppFeatureType(): string;

  @action
  private async fetchHubApps() {
    if (!directoryStore.trackerId) {
      return;
    }

    try {
      this._hubApps = [];
      this._hubAppsError = null;
      this._hubAppsLoading = true;
      const appInstallationsResponse = await ZipApi.listAppInstalls(directoryStore.trackerId, this.getAppFeatureType());

      if (appInstallationsResponse.installations) {
        // An installation can point at a version that is no longer in the directory (abandoned or
        // unpublished), which 404s. The app is still installed and still usable as a sync
        // endpoint, so degrade that one entry instead of discarding every app we did resolve.
        // Nothing is missing from the user's point of view, so this is logged rather than surfaced.
        const degraded: string[] = [];
        const hubAppPromises = appInstallationsResponse.installations.map(async (installation) => {
          try {
            const appResponse = await ZipApi.fetchAppVersion(installation.app_id, installation.version);
            return {
              name: appResponse.app_version.metadata.display_name,
              icon_url: appResponse.app_version.metadata.icon_url,
              app_id: installation.app_id,
              version: installation.version
            } as HubApp;
          } catch (error: any) {
            degraded.push(`${installation.app_id}@${installation.version}`);
            console.warn(
              `Unable to load app version details for ${installation.app_id}@${installation.version};` +
              ' falling back to installation metadata.',
              error
            );
            return {
              name: installation.app_id,
              icon_url: FALLBACK_APP_ICON,
              app_id: installation.app_id,
              version: installation.version
            } as HubApp;
          }
        });

        this._hubApps = await Promise.all(hubAppPromises);
        if (degraded.length > 0) {
          // Resolution order is nondeterministic; sort so the log line is stable.
          console.warn(
            `${degraded.length} of ${appInstallationsResponse.installations.length} installed apps ` +
            `are listed with fallback metadata: ${degraded.sort().join(', ')}`
          );
        }
      }
    } catch (error: any) {
      this._hubAppsError = await getApiErrorMessage(error, 'Unable to load apps');
    } finally {
      this._hubAppsLoading = false;
    }
  }
}
